import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_COMMON_VOTES } from '../graphql/commonVotes';
import VotingDetailModal from './VotingDetailModal';

/**
 * 聊天室共同話題下拉組件
 * 顯示最近的共同觀點，點擊可查看投票詳情
 */
const CommonTopicsDropdown = ({ chatroomId, targetUserId, targetUsername }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedVotingId, setSelectedVotingId] = useState(null);
  const [isRandomMode, setIsRandomMode] = useState(false);

  // 查詢共同觀點（固定按對方投票時間排序）
  const { data: sameTopics, loading: sameLoading, refetch: refetchTopics } = useQuery(GET_COMMON_VOTES, {
    variables: {
      targetUserId,
      limit: 5,
      skip: 0,
      sameOptionOnly: true,
      sortBy: isRandomMode ? 'RANDOM' : 'TARGET_USER' // 根據模式選擇排序方式
    },
    skip: !isOpen || !targetUserId,
    fetchPolicy: 'network-only', // 每次打開都重新獲取，確保隨機性
  });

  // 處理隨機推薦按鈕點擊
  const handleRandomRecommend = async () => {
    setIsRandomMode(true);
    // 使用 refetch 重新獲取隨機排序的話題
    // 明確傳遞 RANDOM 排序參數，確保立即獲取新的隨機結果
    await refetchTopics({
      targetUserId,
      limit: 5,
      skip: 0,
      sameOptionOnly: true,
      sortBy: 'RANDOM'
    });
  };

  // 處理話題點擊 - 打開投票詳情彈窗
  const handleTopicClick = (topic) => {
    const { userVote } = topic;
    if (userVote?.articleId) {
      setSelectedVotingId(userVote.articleId);
      setIsOpen(false); // 關閉下拉框
    }
  };

  const currentTopics = sameTopics?.commonVotes?.commonVotes || [];
  const loading = sameLoading;

  return (
    <div className="relative">
      {/* 觸發按鈕 */}
      <button
        type="button"
        onClick={() => {
          setIsOpen(!isOpen);
          if (isOpen) {
            // 關閉時重置隨機模式
            setIsRandomMode(false);
          }
        }}
        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
        title="插入共同話題"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      </button>

      {/* 下拉內容 */}
      {isOpen && (
        <div className="absolute bottom-full left-0 mb-2 w-[calc(100vw-2rem)] sm:w-96 max-w-96 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-96 overflow-hidden flex flex-col">
          {/* 標題欄 */}
          <div className="bg-gradient-to-r from-green-50 to-purple-50 px-4 py-3 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-800">
                與 {targetUsername} 的共同觀點
              </h3>
              <button
                onClick={() => {
                  setIsOpen(false);
                  setIsRandomMode(false); // 關閉時重置隨機模式
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* 話題列表 */}
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : currentTopics.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-2 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <p className="text-sm">暫無共同觀點</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {currentTopics.map((topic, index) => (
                  <TopicItem
                    key={topic.question.id}
                    topic={topic}
                    index={index}
                    onClick={() => handleTopicClick(topic)}
                    showAnswer={true}
                  />
                ))}
              </div>
            )}
          </div>

          {/* 隨機推薦按鈕和提示信息 */}
          <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 space-y-2">
            {/* 隨機推薦按鈕 */}
            <button
              onClick={handleRandomRecommend}
              disabled={loading || currentTopics.length === 0}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-medium rounded-lg hover:from-purple-600 hover:to-pink-600 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>🎲 隨機推薦</span>
            </button>
            {/* 提示信息 */}
            <p className="text-xs text-gray-500 text-center">
              💡 點擊查看投票詳情
            </p>
          </div>
        </div>
      )}

      {/* 投票詳情彈窗 */}
      <VotingDetailModal
        isOpen={selectedVotingId !== null}
        onClose={() => setSelectedVotingId(null)}
        votingId={selectedVotingId}
      />
    </div>
  );
};

/**
 * 單個話題項組件
 */
const TopicItem = ({ topic, index, onClick, showAnswer }) => {
  const { question, userVote, isSameOption, mostRecentVotedAt } = topic;
  
  // 計算時間差
  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    
    if (seconds < 60) return '剛剛';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}分鐘前`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}小時前`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}天前`;
    return `${Math.floor(seconds / 604800)}周前`;
  };

  return (
    <button
      onClick={onClick}
      className="w-full px-4 py-3 text-left hover:bg-green-50 transition-colors"
    >
      <div className="flex items-start gap-3">
        {/* 序號 */}
        <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold bg-green-100 text-green-700">
          {index + 1}
        </div>

        <div className="flex-1 min-w-0">
          {/* 問題標題 */}
          <div className="flex items-start justify-between gap-2">
            <p className="font-medium text-sm text-gray-800 line-clamp-2">
              {question.title}
            </p>
            <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              一致
            </span>
          </div>

          {/* 共同選擇 */}
          {showAnswer && (
            <div className="mt-2 flex items-center gap-1.5">
              <span className="text-xs font-medium text-green-600">共同選擇：</span>
              <span className="text-xs text-gray-700 bg-green-50 px-2 py-0.5 rounded">
                {question.options[userVote.selectedOption].text}
              </span>
            </div>
          )}

          {/* 時間和標籤 */}
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-400">
              {getTimeAgo(mostRecentVotedAt)}
            </span>
            {question.tags && question.tags.length > 0 && (
              <>
                <span className="text-gray-300">•</span>
                <div className="flex gap-1">
                  {question.tags.slice(0, 2).map((tag, idx) => (
                    <span
                      key={tag?.id || idx}
                      className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded"
                    >
                      #{tag?.name || tag}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </button>
  );
};

export default CommonTopicsDropdown; 